/**
 * Havn pre-checkout page: apply discount codes + save draft fields to cart attributes, then go to Shopify checkout.
 */
(function () {
  const root = document.querySelector('[data-havn-checkout-preview]');
  if (!root) return;

  const checkoutUrl = root.dataset.checkoutUrl || '/checkout';
  const cartUpdateUrl = root.dataset.cartUpdateUrl || '/cart/update.js';
  const minCheckoutQty = Math.max(1, parseInt(root.dataset.minCheckoutQuantity || '10', 10));

  const discountInput = root.querySelector('[data-havn-discount-input]');
  const discountBtn = root.querySelector('[data-havn-discount-apply]');
  const discountMsg = root.querySelector('[data-havn-discount-message]');
  const placeBtn = root.querySelector('[data-havn-place-order]');

  function setMsg(text, isError) {
    if (!discountMsg) return;
    discountMsg.textContent = text || '';
    discountMsg.dataset.error = isError ? 'true' : 'false';
  }

  function formatMinCheckoutMessage(count) {
    const tpl =
      typeof Theme !== 'undefined' && Theme.translations?.cart_minimum_checkout_notice
        ? Theme.translations.cart_minimum_checkout_notice
        : 'Add at least __MIN__ items to check out. You currently have __CUR__.';
    return tpl.replaceAll('__MIN__', String(minCheckoutQty)).replaceAll('__CUR__', String(count));
  }

  /** @param {HTMLElement | null | undefined} notice */
  function setMinQtyNoticeHtml(notice, text) {
    if (!notice) return;
    notice.replaceChildren();
    if (!text) return;
    const emphasis = document.createElement('strong');
    emphasis.className = 'havn-checkout-preview__min-qty-notice-text';
    emphasis.textContent = text;
    notice.appendChild(emphasis);
  }

  async function getCartJson() {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('Could not load cart');
    return r.json();
  }

  async function applyDiscount() {
    const code = (discountInput?.value || '').trim();
    if (!code) {
      setMsg('Enter a code', true);
      return;
    }
    setMsg('Applying…', false);
    discountBtn?.setAttribute('disabled', 'disabled');

    try {
      const cart = await getCartJson();
      const existing = (cart.discount_codes || []).map((/** @type {{ code: string }} */ d) => d.code);
      if (existing.map((c) => c.toLowerCase()).includes(code.toLowerCase())) {
        setMsg('That code is already applied', true);
        return;
      }
      const merged = [...existing, code].join(',');

      const res = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: merged }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setMsg(data.message || data.description || 'Could not apply code', true);
        return;
      }

      const bad = (data.discount_codes || []).find(
        (/** @type {{ code: string; applicable: boolean }} */ d) =>
          d.code.toLowerCase() === code.toLowerCase() && d.applicable === false
      );
      if (bad) {
        setMsg('That code is not valid for this cart', true);
        return;
      }

      window.location.reload();
    } catch (e) {
      setMsg('Something went wrong. Try again.', true);
    } finally {
      discountBtn?.removeAttribute('disabled');
    }
  }

  async function saveAttributesAndCheckout() {
    let cart;
    try {
      cart = await getCartJson();
    } catch {
      return;
    }

    const count = cart.item_count ?? 0;
    root.dataset.cartCount = String(count);

    if (count < minCheckoutQty) {
      const notice = root.querySelector('[data-havn-min-qty-notice]');
      if (notice) {
        setMinQtyNoticeHtml(notice, formatMinCheckoutMessage(count));
        notice.hidden = false;
      }
      placeBtn?.setAttribute('aria-describedby', 'havn-checkout-preview-min-qty-notice');
      return;
    }

    placeBtn?.setAttribute('disabled', 'disabled');
    placeBtn?.setAttribute('aria-busy', 'true');

    let shouldRedirect = false;
    try {
      const attrs = {
        checkout_email: root.querySelector('#havn-checkout-email')?.value?.trim() || '',
        checkout_first_name: root.querySelector('#havn-checkout-first')?.value?.trim() || '',
        checkout_last_name: root.querySelector('#havn-checkout-last')?.value?.trim() || '',
        checkout_address1: root.querySelector('#havn-checkout-address')?.value?.trim() || '',
        checkout_address2: root.querySelector('#havn-checkout-apartment')?.value?.trim() || '',
        checkout_city: root.querySelector('#havn-checkout-city')?.value?.trim() || '',
        checkout_province: root.querySelector('#havn-checkout-state')?.value?.trim() || '',
        checkout_zip: root.querySelector('#havn-checkout-zip')?.value?.trim() || '',
        checkout_marketing:
          root.querySelector('.havn-checkout-preview__check input[type="checkbox"]')?.checked === true ? 'yes' : 'no',
      };

      try {
        await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: attrs }),
        });
      } catch (_) {
        /* still send user to checkout */
      }

      shouldRedirect = true;
      window.location.href = checkoutUrl;
    } finally {
      if (!shouldRedirect) {
        placeBtn?.removeAttribute('aria-busy');
        placeBtn?.removeAttribute('disabled');
      }
    }
  }

  discountBtn?.addEventListener('click', applyDiscount);
  discountInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyDiscount();
    }
  });
  placeBtn?.addEventListener('click', saveAttributesAndCheckout);
})();

/** Cart page: open / close checkout popup (section with data-havn-checkout-modal). */
(function () {
  const scrollLockKey = 'data-havn-scroll-y';

  /** Section lives after </main>; browsers scroll the page to it on focus unless we lock position. */
  function restoreViewportScroll(y) {
    window.scrollTo(0, y);
  }

  function onDocumentScrollWhileModalOpen() {
    if (!document.documentElement.classList.contains('havn-checkout-modal-open')) return;
    const raw = document.body.getAttribute(scrollLockKey);
    const y = raw !== null ? parseInt(raw, 10) || 0 : 0;
    if (window.scrollY !== y) {
      restoreViewportScroll(y);
    }
  }

  window.addEventListener('scroll', onDocumentScrollWhileModalOpen, { passive: true });

  function closeModal(modal) {
    modal.setAttribute('hidden', '');
    const y = document.body.getAttribute(scrollLockKey);
    document.body.classList.remove('havn-checkout-modal-open');
    document.documentElement.classList.remove('havn-checkout-modal-open');
    document.body.style.overflow = '';
    document.body.removeAttribute(scrollLockKey);
    document.documentElement.style.overflow = '';
    if (y !== null) {
      restoreViewportScroll(parseInt(y, 10) || 0);
    }
  }

  function openModal(modal) {
    /* Do not move the modal to document.body: section {% stylesheet %} is scoped to #shopify-section-*,
       and moving the node breaks those rules so the overlay loses position:fixed and appears in-flow.
       Avoid body { position: fixed } here, it can change the containing block for nested position:fixed
       sections and hide the dialog while leaving the backdrop visible. */
    const scrollY = window.scrollY;
    document.body.setAttribute(scrollLockKey, String(scrollY));
    document.body.classList.add('havn-checkout-modal-open');
    document.documentElement.classList.add('havn-checkout-modal-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    modal.removeAttribute('hidden');

    /* Keep the cart / hero in place: focus + layout can trigger scroll-into-view of the section below the fold */
    restoreViewportScroll(scrollY);
    requestAnimationFrame(() => {
      restoreViewportScroll(scrollY);
      requestAnimationFrame(() => restoreViewportScroll(scrollY));
    });
    setTimeout(() => restoreViewportScroll(scrollY), 0);
    setTimeout(() => restoreViewportScroll(scrollY), 100);

    const focusable = modal.querySelector(
      'input:not([disabled]), button:not([disabled]):not([data-havn-close-checkout-modal])'
    );
    if (focusable && typeof focusable.focus === 'function') {
      try {
        focusable.focus({ preventScroll: true });
      } catch (_) {
        focusable.focus();
      }
      restoreViewportScroll(scrollY);
    }
  }

  function getMinCheckoutQty() {
    const el = document.querySelector('[data-havn-checkout-preview]');
    return Math.max(1, parseInt(el?.dataset?.minCheckoutQuantity || '10', 10));
  }

  function formatMinCheckoutMessage(min, count) {
    const tpl =
      typeof Theme !== 'undefined' && Theme.translations?.cart_minimum_checkout_notice
        ? Theme.translations.cart_minimum_checkout_notice
        : 'Add at least __MIN__ items to check out. You currently have __CUR__.';
    return tpl.replaceAll('__MIN__', String(min)).replaceAll('__CUR__', String(count));
  }

  /** @param {HTMLElement | null | undefined} notice */
  function setMinQtyNoticeHtml(notice, text) {
    if (!notice) return;
    notice.replaceChildren();
    if (!text) return;
    const emphasis = document.createElement('strong');
    emphasis.className = 'havn-checkout-preview__min-qty-notice-text';
    emphasis.textContent = text;
    notice.appendChild(emphasis);
  }

  async function syncCheckoutPreviewWithCart() {
    const previewRoot = document.querySelector('[data-havn-checkout-preview]');
    if (!previewRoot) return;
    const min = getMinCheckoutQty();
    let count = 0;
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const cart = await r.json();
      count = cart.item_count ?? 0;
    } catch {
      return;
    }
    previewRoot.dataset.cartCount = String(count);
    const placeOrderBtn = previewRoot.querySelector('[data-havn-place-order]');
    const notice = previewRoot.querySelector('[data-havn-min-qty-notice]');
    const ok = count >= min;
    if (notice) {
      if (count > 0) {
        setMinQtyNoticeHtml(notice, formatMinCheckoutMessage(min, count));
      } else {
        setMinQtyNoticeHtml(notice, '');
      }
      notice.hidden = count < 1 || ok;
    }
    if (placeOrderBtn) {
      if (count < 1) {
        placeOrderBtn.disabled = true;
        placeOrderBtn.setAttribute('aria-disabled', 'true');
      } else {
        placeOrderBtn.disabled = !ok;
        placeOrderBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');
        if (ok) {
          placeOrderBtn.removeAttribute('aria-describedby');
        } else {
          placeOrderBtn.setAttribute('aria-describedby', 'havn-checkout-preview-min-qty-notice');
        }
      }
    }
  }

  let syncTimer;
  function scheduleSyncCheckoutPreview() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      void syncCheckoutPreviewWithCart();
    }, 80);
  }

  document.addEventListener('cart:update', scheduleSyncCheckoutPreview);
  void syncCheckoutPreviewWithCart();

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-havn-open-checkout-modal]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;

    void (async () => {
      const min = getMinCheckoutQty();
      let count = 0;
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (r.ok) {
          const cart = await r.json();
          count = cart.item_count ?? 0;
        }
      } catch {
        if (btn.dataset.havnCheckoutFallbackUrl) window.location.href = btn.dataset.havnCheckoutFallbackUrl;
        return;
      }
      if (count < min) return;
      const m = document.querySelector('[data-havn-checkout-modal]');
      if (m) openModal(m);
      else if (btn.dataset.havnCheckoutFallbackUrl) window.location.href = btn.dataset.havnCheckoutFallbackUrl;
    })();
  });

  const modal = document.querySelector('[data-havn-checkout-modal]');
  if (!modal) return;

  modal.querySelectorAll('[data-havn-close-checkout-modal]').forEach((el) => {
    el.addEventListener('click', () => closeModal(modal));
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal(modal);
  });
})();
